import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FlaskConical, Send, RotateCcw, Loader2, ExternalLink } from "lucide-react";
import { testAssistant } from "@/lib/dentiumApi";
import { errText } from "@/components/training/trainingUtils";

const SUGGESTIONS = ["¿Abrís mañana?", "¿Tenéis alguna promoción?", "¿Cuánto cuesta un blanqueamiento?"];

export const TestAssistantCard = () => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const ask = useMutation({
    mutationFn: (m) => testAssistant({ message: m, history: msgs.map(({ role, text: t }) => ({ role, text: t })) }),
    onSuccess: (d) => setMsgs((s) => [...s, { role: "bot", text: d.reply, button: d.button, source: d.source }]),
    onError: (e) => setMsgs((s) => [...s, { role: "bot", text: errText(e, "No se pudo obtener respuesta. Inténtalo de nuevo."), source: "error" }]),
  });
  const send = (m) => {
    const t = (m ?? text).trim();
    if (!t || ask.isPending) return;
    setMsgs((s) => [...s, { role: "user", text: t }]);
    setText("");
    ask.mutate(t);
  };

  return (
    <div className="rounded-2xl border border-[#E7E2DA] bg-white flex flex-col overflow-hidden" data-testid="test-assistant-card">
      <div className="px-5 py-3.5 border-b border-[#EDE7DE] flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-[#1A1A1A]" />
        <h2 className="text-sm font-semibold text-[#1A1A1A]">Probar al asistente</h2>
        <span className="text-[11px] text-[#A79E90]">· no se guarda ni se envía a pacientes</span>
        {msgs.length > 0 && (
          <button data-testid="test-assistant-reset" onClick={() => setMsgs([])} className="ml-auto text-xs text-[#8A7E70] hover:text-[#1A1A1A] inline-flex items-center gap-1"><RotateCcw className="h-3.5 w-3.5" /> Empezar de nuevo</button>
        )}
      </div>
      <div className="flex-1 min-h-[260px] max-h-[420px] overflow-y-auto px-4 py-4 space-y-2.5 bg-[#FAF8F5]" data-testid="test-assistant-thread">
        {msgs.length === 0 && (
          <div className="text-center pt-6">
            <p className="text-sm text-[#6B6157] mb-3">Escribe como si fueras un paciente y comprueba lo que ha aprendido.</p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="text-xs rounded-full border border-[#E7E2DA] bg-white hover:bg-[#F1EBE1] px-3 py-1.5 text-[#4A4239]">{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
            <div data-testid={m.role === "user" ? "test-msg-user" : "test-msg-bot"}
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-white border border-[#EDE7DE] text-[#1A1A1A]" : "bg-[#1A1A1A] text-white"}`}>
              {m.text}
              {m.button && (
                <a href={m.button.url} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-white/15 hover:bg-white/25 px-3 py-1.5 text-xs font-semibold">
                  <ExternalLink className="h-3.5 w-3.5" /> {m.button.label}
                </a>
              )}
              {m.source === "respuesta_automatica" && <div className="text-[10px] opacity-60 mt-1">Respuesta automática (sin IA)</div>}
            </div>
          </div>
        ))}
        {ask.isPending && <div className="flex justify-end"><div className="rounded-2xl bg-[#1A1A1A] text-white px-3.5 py-2"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="border-t border-[#EDE7DE] flex items-center gap-2 px-3 py-2.5">
        <input data-testid="test-assistant-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Pregunta como un paciente…" className="flex-1 min-w-0 border border-[#E7E2DA] rounded-full px-4 py-2 text-sm outline-none focus:border-[#1A1A1A]" />
        <button data-testid="test-assistant-send" onClick={() => send()} disabled={!text.trim() || ask.isPending}
          className="h-10 w-10 shrink-0 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
};
